// CPOC-39: the shopper's cart and the pages they visited, for the rep (TRACI-1011: the
// LivePerson rep's handoff message as well as the Connect POC rep desk).
//
// The embed loads on every tirerack.com page and is answered with that page's context
// (TRACI_PAGE_CONTEXT from the loader). Each answer is one page visit, and on the cart
// page it also carries the site's own LivePerson cart record (the SDE the cart page hands
// LivePerson): as `lpCart` when the loader read it, else inline in the page HTML we are
// sent. We keep the last 15 pages and the newest cart in this tab's sessionStorage, so a handoff from any
// later page can carry them. Nothing here makes a request; the data only leaves inside
// the handoff TRACI already sends, and only reaches the rep the shopper asked for.
//
// TRACI-1011: on in every environment (it was dev and local only, which left prod reps
// with LivePerson's view alone). VITE_SHOPPER_JOURNEY=off at build time turns it off.

import { lpCartFromPageHtml } from './lpMonitor';

export type JourneyPage = { url: string; title: string; at: number };
export type JourneyCartLine = { sku: string; name: string; qty?: number; price?: number };
export type JourneyCart = { items: JourneyCartLine[]; total?: number; at: number; count?: number };
export type Journey = { pages: JourneyPage[]; cart: JourneyCart | null };

const STORAGE_SLOT = 'traci.journey.v1';
const SITE_SLOT = 'traci.site.v1';
const SITE_VALUES_SLOT = 'traci.sitevalues.v1';
export const MAX_PAGES = 15;
/** CPOC-154: 20 pages on dev only; every other env keeps MAX_PAGES. */
export const maxPages = (env: string | undefined): number => ((env || '').trim() === 'dev' ? 20 : MAX_PAGES);
export const MAX_CART_LINES = 10;
/** A cart record older than this is a guess, not the cart: it is left out of the handoff. */
export const CART_MAX_AGE_MS = 2 * 60 * 60 * 1000;

/* eslint-disable @typescript-eslint/no-explicit-any */
const text = (v: unknown, n: number): string =>
  typeof v === 'string' || typeof v === 'number' ? String(v).replace(/\s+/g, ' ').trim().slice(0, n) : '';
const amount = (v: unknown): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) && v >= 0 && v < 1_000_000 ? Math.round(v * 100) / 100 : undefined;

/** Scheme, host and path only: a query or fragment can carry an email, a zip or an order number. */
export function pagePath(url: unknown): string {
  try {
    const u = new URL(String(url || ''));
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return '';
    return `${u.protocol}//${u.host}${u.pathname}`.slice(0, 300);
  } catch {
    return '';
  }
}

/** The site's cart SDE ({products: [{product: {name, sku, price}, quantity}], total}) or an array of them, newest last. */
export function normalizeCart(raw: unknown, now: number): JourneyCart | null {
  const rec: any = Array.isArray(raw) ? raw[raw.length - 1] : raw;
  if (!rec || typeof rec !== 'object' || !Array.isArray(rec.products)) return null;
  const items: JourneyCartLine[] = [];
  for (const p of rec.products.slice(0, MAX_CART_LINES)) {
    const prod: any = p && typeof p === 'object' ? (p.product && typeof p.product === 'object' ? p.product : p) : null;
    if (!prod) continue;
    const line: JourneyCartLine = { sku: text(prod.sku, 40), name: text(prod.name, 80) };
    const qty = Number(p.quantity);
    if (Number.isInteger(qty) && qty > 0 && qty < 1000) line.qty = qty;
    const price = amount(typeof prod.price === 'string' ? Number(prod.price) : prod.price);
    if (price !== undefined) line.price = price;
    if (line.sku || line.name) items.push(line);
  }
  const cart: JourneyCart = { items, at: now };
  const total = amount(typeof rec.total === 'string' ? Number(rec.total) : rec.total);
  if (total !== undefined) cart.total = total;
  return cart;
}

export function enabledFor(
  _env: string | undefined,
  flag: string | undefined = import.meta.env.VITE_SHOPPER_JOURNEY,
): boolean {
  return (flag || '').trim().toLowerCase() !== 'off';
}

/** CPOC-153: the site's own zip, tire size, order number, pageJourney and cart ride the handoff on dev only. */
export function siteValuesEnabled(env: string | undefined): boolean {
  return (env || '').trim() === 'dev';
}

export type SiteValues = { zip: string; tireSize: { front: string; rear: string } | null; orderNumber: string };

function readSiteValues(store: Storage | null): SiteValues {
  try {
    const s = JSON.parse((store && store.getItem(SITE_VALUES_SLOT)) || 'null');
    if (s && typeof s === 'object') {
      const t = s.tireSize;
      return {
        zip: typeof s.zip === 'string' ? s.zip : '',
        tireSize: t && typeof t.front === 'string' && t.front ? { front: t.front, rear: typeof t.rear === 'string' ? t.rear : '' } : null,
        orderNumber: typeof s.orderNumber === 'string' ? s.orderNumber : '',
      };
    }
  } catch { /* a bad value starts over */ }
  return { zip: '', tireSize: null, orderNumber: '' };
}

/** CPOC-153: record the site's zip, tire size and order number from one page-context message. */
function recordSiteValues(store: Storage | null, data: any): void {
  const next = readSiteValues(store);
  const has = (k: string) => !!data && typeof data === 'object' && Object.prototype.hasOwnProperty.call(data, k);
  // One rule: a message that carries the key sets it (an empty value clears it); one without keeps what is stored.
  if (has('zip')) next.zip = text(data.zip, 12);
  if (has('tireSize')) {
    const ts = data.tireSize;
    const front = ts && typeof ts === 'object' ? text(ts.front, 40) : '';
    next.tireSize = front ? { front, rear: text(ts.rear, 40) } : null;
  }
  if (has('ecommOrderId') || has('ecommid')) next.orderNumber = text(data.ecommOrderId, 40) || text(data.ecommid, 40);
  try { store?.setItem(SITE_VALUES_SLOT, JSON.stringify(next)); } catch { /* storage full or blocked */ }
}

/** The site's own values for the handoff (the caller checks siteValuesEnabled). Never part of Journey. */
export function siteValuesForHandoff(store: Storage | null = session()): SiteValues {
  return readSiteValues(store);
}

/** The site's pageJourney as pages: query stripped, adjacent repeats collapsed, titles kept by url. */
function pagesFromSite(raw: unknown, existing: JourneyPage[], curUrl: string, curTitle: string, now: number, limit: number = MAX_PAGES): JourneyPage[] | null {
  if (!Array.isArray(raw) || !raw.length) return null;
  const out: JourneyPage[] = [];
  let lastRaw = '';
  for (const e of raw) {
    const rawUrl = String((e as any)?.url ?? '');
    const url = pagePath(rawUrl);
    if (!url) continue;
    const t = Date.parse(String((e as any)?.viewedAt));
    const at = Number.isFinite(t) ? t : undefined;
    const title = (url === curUrl && curTitle) || existing.find((p) => p.url === url)?.title || '';
    // Collapse only an identical raw URL: tire A then tire B share a path and differ by query.
    if (rawUrl === lastRaw) {
      const prev = out[out.length - 1];
      if (title) prev.title = title;
      continue;
    }
    lastRaw = rawUrl;
    out.push({ url, title, at: at ?? existing.find((p) => p.url === url)?.at ?? now });
  }
  return out.length ? out.slice(-limit) : null;
}

/** The site's cart.lineItems ([{sku, quantity, unitPrice}]) as the journey cart. */
function cartFromSite(lineItems: unknown[], now: number): JourneyCart {
  const lines: JourneyCartLine[] = [];
  let complete = true;
  for (const li of lineItems) {
    if (!li || typeof li !== 'object') continue;
    const sku = text((li as any).sku, 40);
    if (!sku) continue;
    const line: JourneyCartLine = { sku, name: '' };
    const q = Number((li as any).quantity);
    if (Number.isInteger(q) && q > 0 && q < 1000) line.qty = q;
    const rawPrice = (li as any).unitPrice;
    const price = amount(typeof rawPrice === 'string' && rawPrice.trim() ? Number(rawPrice) : rawPrice);
    if (price !== undefined) line.price = price;
    if (line.qty === undefined || line.price === undefined) complete = false;
    lines.push(line);
  }
  const cart: JourneyCart = { items: lines.slice(0, MAX_CART_LINES), at: now };
  if (lines.length && complete) {
    cart.total = Math.round(lines.reduce((n, l) => n + (l.qty as number) * (l.price as number), 0) * 100) / 100;
  }
  return cart;
}

function read(store: Storage | null): Journey {
  try {
    const j = JSON.parse((store && store.getItem(STORAGE_SLOT)) || 'null');
    if (j && Array.isArray(j.pages)) return { pages: j.pages, cart: j.cart || null };
  } catch { /* a bad value starts over */ }
  return { pages: [], cart: null };
}

function write(store: Storage | null, j: Journey): void {
  try { store?.setItem(STORAGE_SLOT, JSON.stringify(j)); } catch { /* storage full or blocked: nothing to keep */ }
}

/** Record one page-context answer. A repeat answer for the page we are on is not a new visit. */
export function recordContext(store: Storage | null, data: any, now = Date.now(), env?: string): Journey {
  const j = read(store);
  const url = pagePath(data?.url);
  const siteOn = siteValuesEnabled(env);
  const sitePages = siteOn ? pagesFromSite(data?.pageJourney, j.pages, url, text(data?.title, 80), now, maxPages(env)) : null;
  if (sitePages) {
    j.pages = sitePages;
  } else if (url) {
    const last = j.pages[j.pages.length - 1];
    const title = text(data?.title, 80);
    if (last && last.url === url) {
      if (title) last.title = title;
    } else {
      j.pages.push({ url, title, at: now });
    }
    j.pages = j.pages.slice(-maxPages(env));
  }
  const raw = data?.lpCart || lpCartFromPageHtml(data?.html);
  if (siteOn && Array.isArray(data?.cart?.lineItems)) {
    j.cart = cartFromSite(data.cart.lineItems, now);
  } else if (raw) {
    const cart = normalizeCart(raw, now);
    if (cart) j.cart = cart;
  } else if (typeof data?.html === 'string' && /\/cart\//i.test(url)) {
    j.cart = { items: [], at: now };  // the cart page with no cart record: the cart is empty
  }
  write(store, j);
  if (siteOn) recordSiteValues(store, data);
  return j;
}

/** What rides on the handoff: the pages in the order visited, and the cart unless it is stale.
 *  CPOC-110: plus the site header's item count, which is known on every page (cart lines only on
 *  the cart page): with no fresh lines the cart is `{items: [], count}`, never an "empty" cart. */
export function journeyForHandoff(store: Storage | null, now = Date.now(), env?: string): Journey {
  const j = read(store);
  let cart = j.cart && now - (j.cart.at || 0) <= CART_MAX_AGE_MS ? j.cart : null;
  const count = readSite(store).cartCount;
  if (count && now - count.at <= CART_MAX_AGE_MS) {
    cart = cart ? { ...cart, count: count.n } : { items: [], at: count.at, count: count.n };
  }
  return { pages: j.pages.slice(-maxPages(env)), cart };
}

// ---- CPOC-110 / CPOC-111: what only the site's own page can see ------------------------------
// loader.js posts TRACI_SITE_STATE whenever the header's settled item count or the page's own
// LivePerson session changes. The count fills the cart before the shopper opens the cart page;
// the session lets the server read the site's LivePerson visit instead of the embed's own one
// (7% of prod chats got an embed-only session). Live tirerack.com does not run loader.js yet,
// so on prod this stays empty until it does (or the site's buildAIAssistData carries the same).

export type SiteLp = { site: string; visitorId: string; sessionId: string; at: number };
export type SiteState = { cartCount: { n: number; at: number } | null; lp: SiteLp | null };
/** LivePerson ends a visit after 30 idle minutes; an older session is not worth a read. */
export const SITE_LP_MAX_AGE_MS = 30 * 60 * 1000;
const LP_ID = /^[A-Za-z0-9_.-]{1,80}$/;

function readSite(store: Storage | null): SiteState {
  try {
    const s = JSON.parse((store && store.getItem(SITE_SLOT)) || 'null');
    if (s && typeof s === 'object') return { cartCount: s.cartCount || null, lp: s.lp || null };
  } catch { /* a bad value starts over */ }
  return { cartCount: null, lp: null };
}

/** Record one TRACI_SITE_STATE. Returns true when the cart count changed (the live desk wants it). */
export function recordSiteState(store: Storage | null, data: any, now = Date.now()): boolean {
  const s = readSite(store);
  const before = s.cartCount?.n ?? null;
  // Every message re-stamps what it confirms (loader.js repeats unchanged state every few minutes,
  // so a shopper who sits on one page keeps it). null = the header has not settled yet on this page:
  // keep what we had. 0 = the header says the cart is empty: no count.
  const n = data?.cartCount;
  if (Number.isInteger(n) && n > 0 && n < 1000) s.cartCount = { n, at: now };
  else if (n === 0) s.cartCount = null;
  const lp = data?.lpSite;
  if (lp && typeof lp === 'object' && /^[0-9]{1,20}$/.test(String(lp.site)) && LP_ID.test(String(lp.visitorId))
      && LP_ID.test(String(lp.sessionId))) {
    s.lp = { site: String(lp.site), visitorId: String(lp.visitorId), sessionId: String(lp.sessionId), at: now };
  }
  try { store?.setItem(SITE_SLOT, JSON.stringify(s)); } catch { /* storage full or blocked */ }
  return (s.cartCount?.n ?? null) !== before;
}

/** The page's own LivePerson session for the handoff, unless it is too old to still be open. */
export function siteLpForHandoff(store: Storage | null = session(), now = Date.now()): SiteLp | null {
  const lp = readSite(store).lp;
  return lp && now - lp.at <= SITE_LP_MAX_AGE_MS ? lp : null;
}

/** Site state carries a LivePerson session, so on prod it comes only from a tirerack.com page. */
export function trustedSiteOrigin(origin: string, env: string | undefined): boolean {
  if (!trustedParent(origin)) return false;
  return (env || '').trim() !== 'prod' || /^https:\/\/([a-z0-9-]+\.)*tirerack\.com$/i.test(origin);
}

function session(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

/** Only Tire Rack pages (and a local test page) may add to the journey. */
export function trustedParent(origin: string): boolean {
  return /^https:\/\/([a-z0-9-]+\.)*tirerack\.(com|net)$/i.test(origin) || /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/.test(origin);
}

let installed = false;
const changeListeners = new Set<() => void>();

/** CPOC-123: called after every page or cart the recorder keeps (the live Connect desk update). */
export function onJourneyChange(cb: () => void): () => void {
  changeListeners.add(cb);
  return () => { changeListeners.delete(cb); };
}

/** Listen for the parent page's context answers (the same messages EmbedPage reads). */
export function installJourneyRecorder(env: string | undefined = import.meta.env.VITE_ENV as string | undefined): void {
  if (installed || typeof window === 'undefined' || !enabledFor(env) || window.parent === window) return;
  installed = true;
  window.addEventListener('message', (event: MessageEvent) => {
    const d: any = event.data;
    if (event.source !== window.parent || !d || d.source !== 'TR-Parent') return;
    if (d.type === 'TRACI_SITE_STATE') {
      if (!trustedSiteOrigin(event.origin, env)) return;
      if (!recordSiteState(session(), d)) return;
    } else if (d.type === 'TRACI_PAGE_CONTEXT' && trustedParent(event.origin)) {
      recordContext(session(), d, Date.now(), env);
    } else {
      return;
    }
    changeListeners.forEach((cb) => { try { cb(); } catch { /* a listener never breaks recording */ } });
  });
}

/** For the handoff body: undefined only when the build switch turned recording off. */
export function handoffJourney(env: string | undefined = import.meta.env.VITE_ENV as string | undefined): Journey | undefined {
  return enabledFor(env) ? journeyForHandoff(session(), Date.now(), env) : undefined;
}
